import React from "react";
import moment from "moment";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip,
  Divider,
  Stack,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  HealthAndSafety as HealthSummaryIcon,
  LocalDrink as WaterIcon,
  DirectionsRun as RunningIcon,
  Favorite as HeartIcon,
  FitnessCenter as WeightIcon,
  Grass as FiberIcon,
  Bedtime as SleepIcon,
} from "@mui/icons-material";
import NewColors from "../../../../../themes/NewColors";
import { getkCal } from "../../../../../components/LibraryFunctions";
import { labelForMicroKey } from "../../../../../utils/nutritionMicroLabels";
import NutritionCharts from "../../clients/NutritionCharts";
import useWaterUnit from "../../../../../hooks/useWaterUnit";
import { useRecoilValue } from "recoil";
import organizationIdSelector from "../../../../../atoms/organizationIdSelector";
import { useOrganizationDataByOrgId } from "../../../../../hooks/useOrganizationData";
import DailyOptionalQuestionsSection from "../../../../../components/organisms/DailyOptionalQuestionsSection";
import { hasAnsweredDailyOptional } from "../../../../../utils/dailyOptionalQuestionsUtils";

/** Same row shape as `useClientNutritionChartData` for shared `NutritionCharts`. */
function mapReportHealthDataToNutritionSeries(healthData) {
  if (!healthData?.length) return [];
  return healthData.map((day) => {
    const rd = day.reportData || {};
    const micro =
      rd.micronutrients && typeof rd.micronutrients === "object"
        ? rd.micronutrients
        : {};
    const fiberVal = typeof micro.fiber === "number" ? micro.fiber : 0;
    let kCal = 0;
    if (typeof rd.kCal === "number" && rd.kCal > 0) {
      kCal = rd.kCal;
    } else if (rd.carbs > 0 || rd.fats > 0 || rd.proteins > 0) {
      kCal = getkCal({
        carbs: rd.carbs,
        fats: rd.fats,
        proteins: rd.proteins,
      });
    }
    const createdOn = day.createdOn?.seconds
      ? new Date(day.createdOn.seconds * 1000)
      : day.createdOn?.toDate?.() || new Date();

    return {
      ...rd,
      micronutrients: micro,
      kCal: parseFloat(Number(kCal).toFixed(2)),
      carbs: parseFloat(Number(rd.carbs || 0).toFixed(2)),
      fats: parseFloat(Number(rd.fats || 0).toFixed(2)),
      proteins: parseFloat(Number(rd.proteins || 0).toFixed(2)),
      weight: parseFloat(Number(rd.weight || 0).toFixed(2)),
      water: parseFloat(Number(rd.water || 0).toFixed(2)),
      sleepHours: parseFloat(Number(rd.sleepHours || 0).toFixed(1)),
      fiber: parseFloat(Number(fiberVal).toFixed(2)),
      steps: parseFloat(Number(rd.steps || 0).toFixed(0)),
      averageHeartRate: parseFloat(Number(rd.averageHeartRate || 0).toFixed(0)),
      createdOn,
      createdOnString: moment(createdOn).format("D MMM"),
    };
  });
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  marginBottom: theme.spacing(3),
  overflow: "visible",
}));

const CardContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  "&:last-child": {
    paddingBottom: theme.spacing(3),
  },
}));

// Fixed MacroProgress component to prevent prop forwarding to DOM
const MacroProgress = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== "macroColor",
})(({ theme, macroColor }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[200],
  "& .MuiLinearProgress-bar": {
    backgroundColor: macroColor,
  },
}));

const NutritionCard = ({ report }) => {
  const { waterLabel } = useWaterUnit();
  const organizationId = useRecoilValue(organizationIdSelector);
  const { organizationData, loading: orgLoading } = useOrganizationDataByOrgId({
    organizationId,
  });
  const healthData = React.useMemo(() => {
    if (!report?.healthData?.length) return [];
    return [...report.healthData].sort((a, b) => {
      const dateA = new Date(a.createdOn.seconds * 1000);
      const dateB = new Date(b.createdOn.seconds * 1000);
      return dateA - dateB;
    });
  }, [report?.healthData]);

  const nutritionChartSeries = React.useMemo(
    () => mapReportHealthDataToNutritionSeries(healthData),
    [healthData],
  );

  const chartClientUid = report?.clientData?.uid || report?.uid;

  if (!report || !report.healthData) return null;
  if (healthData.length === 0) return null;

  // Get nutrition goal
  const nutritionGoal =
    report.clientData.nutritionGoals?.find((goal) => goal.active) || null;

  // Helper functions
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const calculateCaloriesFromMacros = (proteins, carbs, fats) => {
    return Math.round((proteins || 0) * 4 + (carbs || 0) * 4 + (fats || 0) * 9);
  };

  const calculatePercentage = (actual, goal) => {
    if (!actual || !goal) return 0;
    return Math.min(Math.round((actual / goal) * 100), 100);
  };

  const calculateAverages = () => {
    if (healthData.length === 0)
      return {
        proteins: 0,
        carbs: 0,
        fats: 0,
        calories: 0,
        water: 0,
        fiber: 0,
        sleepHours: 0,
      };

    const counts = {
      proteins: 0,
      carbs: 0,
      fats: 0,
      calories: 0,
      water: 0,
      fiber: 0,
      sleepHours: 0,
    };
    const sum = healthData.reduce(
      (acc, day) => {
        const reportData = day.reportData || {};
        const micro = reportData.micronutrients || {};
        const fiber =
          typeof micro.fiber === "number" ? micro.fiber : 0;
        const calories =
          (typeof reportData.kCal === "number" && reportData.kCal > 0
            ? reportData.kCal
            : null) ??
          calculateCaloriesFromMacros(
            reportData.proteins,
            reportData.carbs,
            reportData.fats,
          );

        // Only add to sums if the values are greater than 0
        if (reportData.proteins > 0) {
          acc.proteins += reportData.proteins;
          counts.proteins++;
        }
        if (reportData.carbs > 0) {
          acc.carbs += reportData.carbs;
          counts.carbs++;
        }
        if (reportData.fats > 0) {
          acc.fats += reportData.fats;
          counts.fats++;
        }
        if (calories > 0) {
          acc.calories += calories;
          counts.calories++;
        }
        if (reportData.water > 0) {
          acc.water += reportData.water;
          counts.water++;
        }
        if (fiber > 0) {
          acc.fiber += fiber;
          counts.fiber++;
        }
        if (
          typeof reportData.sleepHours === "number" &&
          reportData.sleepHours > 0
        ) {
          acc.sleepHours += reportData.sleepHours;
          counts.sleepHours++;
        }

        return acc;
      },
      {
        proteins: 0,
        carbs: 0,
        fats: 0,
        calories: 0,
        water: 0,
        fiber: 0,
        sleepHours: 0,
      },
    );

    return {
      proteins:
        counts.proteins > 0 ? Math.round(sum.proteins / counts.proteins) : 0,
      carbs: counts.carbs > 0 ? Math.round(sum.carbs / counts.carbs) : 0,
      fats: counts.fats > 0 ? Math.round(sum.fats / counts.fats) : 0,
      calories:
        counts.calories > 0 ? Math.round(sum.calories / counts.calories) : 0,
      water: counts.water > 0 ? Math.round(sum.water / counts.water) : 0,
      fiber: counts.fiber > 0 ? Math.round(sum.fiber / counts.fiber) : 0,
      sleepHours:
        counts.sleepHours > 0
          ? Math.round((sum.sleepHours / counts.sleepHours) * 10) / 10
          : 0,
    };
  };

  const averages = calculateAverages();

  const otherMicroAverages = React.useMemo(() => {
    const sums = {};
    const counts = {};
    healthData.forEach((day) => {
      const m = day.reportData?.micronutrients;
      if (!m || typeof m !== "object") return;
      Object.keys(m).forEach((k) => {
        if (k === "fiber") return;
        const v = m[k];
        if (typeof v !== "number" || !(v > 0)) return;
        sums[k] = (sums[k] || 0) + v;
        counts[k] = (counts[k] || 0) + 1;
      });
    });
    return Object.keys(sums)
      .map((k) => ({
        key: k,
        avg: counts[k] ? Math.round((sums[k] / counts[k]) * 10) / 10 : 0,
      }))
      .sort((a, b) =>
        labelForMicroKey(a.key).localeCompare(labelForMicroKey(b.key)),
      );
  }, [healthData]);

  const avgSteps = React.useMemo(() => {
    const withSteps = healthData.filter((d) => (d.reportData?.steps || 0) > 0);
    if (!withSteps.length) return 0;
    return Math.round(
      withSteps.reduce((s, d) => s + (d.reportData?.steps || 0), 0) /
        withSteps.length,
    );
  }, [healthData]);

  const daysWithCheckInQuestions = React.useMemo(
    () =>
      healthData.filter((day) =>
        hasAnsweredDailyOptional(day.reportData),
      ),
    [healthData],
  );

  // Calculate average percentages
  const avgProteinPercentage = calculatePercentage(
    averages.proteins,
    nutritionGoal?.proteins,
  );
  const avgCarbsPercentage = calculatePercentage(
    averages.carbs,
    nutritionGoal?.carbs,
  );
  const avgFatsPercentage = calculatePercentage(
    averages.fats,
    nutritionGoal?.fats,
  );

  return (
    <StyledCard>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <HealthSummaryIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              Health Summary
            </Typography>
          </Stack>
        }
      />
      <CardContentWrapper>
        {/* Water & fiber emphasized */}
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                textAlign: "center",
                py: 2,
                px: 2,
                borderRadius: 2,
                bgcolor: "rgba(0, 119, 200, 0.12)",
                border: 1,
                borderColor: "divider",
              }}
            >
              <WaterIcon sx={{ fontSize: 36, mb: 0.5, color: "info.main" }} />
              <Typography variant="h4" fontWeight="bold">
                {waterLabel(averages.water)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average water / day
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                textAlign: "center",
                py: 2,
                px: 2,
                borderRadius: 2,
                bgcolor: "rgba(76, 175, 80, 0.12)",
                border: 1,
                borderColor: "divider",
              }}
            >
              <FiberIcon sx={{ fontSize: 36, mb: 0.5, color: "success.main" }} />
              <Typography variant="h4" fontWeight="bold">
                {averages.fiber > 0 ? `${Math.round(averages.fiber)} g` : "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average fiber / day
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h4"
                fontWeight="bold"
                color="primary.main"
                align="center"
              >
                {Math.round(averages.calories)} kcal
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
              >
                Average Daily Calories
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Chip
                icon={<RunningIcon />}
                label={`${avgSteps.toLocaleString()} steps`}
                color="success"
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Avg Daily Steps
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Chip
                icon={<HeartIcon />}
                label={`${Math.round(
                  healthData.reduce(
                    (sum, day) => sum + (day.reportData?.averageHeartRate || 0),
                    0,
                  ) /
                    (healthData.filter(
                      (day) => day.reportData?.averageHeartRate > 0,
                    ).length || 1),
                )} BPM`}
                color="warning"
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Avg Heart Rate
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Chip
                icon={<WeightIcon />}
                label={`${Math.round(
                  healthData.find((day) => day.reportData?.weight > 0)
                    ?.reportData.weight || 0,
                )} lbs`}
                color="warning"
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Latest Weight
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Chip
                icon={<SleepIcon />}
                label={
                  averages.sleepHours > 0 ? `${averages.sleepHours} h` : "—"
                }
                color={averages.sleepHours > 0 ? "info" : "default"}
                variant={averages.sleepHours > 0 ? "filled" : "outlined"}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Avg Sleep
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Average Macronutrients */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Period Averages
        </Typography>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Protein */}
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Protein
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {Math.round(averages.proteins)}g
                </Typography>
                {nutritionGoal?.proteins && (
                  <Typography variant="body2" color="text.secondary">
                    Goal: {Math.round(nutritionGoal.proteins)}g
                  </Typography>
                )}
              </Box>
              <MacroProgress
                variant="determinate"
                value={avgProteinPercentage}
                macroColor={NewColors.nutrition.proteins}
              />
            </Box>
          </Grid>

          {/* Carbs */}
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Carbs
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {Math.round(averages.carbs)}g
                </Typography>
                {nutritionGoal?.carbs && (
                  <Typography variant="body2" color="text.secondary">
                    Goal: {Math.round(nutritionGoal.carbs)}g
                  </Typography>
                )}
              </Box>
              <MacroProgress
                variant="determinate"
                value={avgCarbsPercentage}
                macroColor={NewColors.nutrition.carbs}
              />
            </Box>
          </Grid>

          {/* Fats */}
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Fats
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {Math.round(averages.fats)}g
                </Typography>
                {nutritionGoal?.fats && (
                  <Typography variant="body2" color="text.secondary">
                    Goal: {Math.round(nutritionGoal.fats)}g
                  </Typography>
                )}
              </Box>
              <MacroProgress
                variant="determinate"
                value={avgFatsPercentage}
                macroColor={NewColors.nutrition.fats}
              />
            </Box>
          </Grid>
        </Grid>

        {chartClientUid && nutritionChartSeries.length > 0 ? (
          <>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Trends
            </Typography>
            <Box sx={{ mb: 1 }}>
              <NutritionCharts
                client={{ uid: chartClientUid }}
                nutritionChartData={nutritionChartSeries}
                useSharedNutritionSeries
                showChartTitle={false}
              />
            </Box>
          </>
        ) : null}

        <Divider sx={{ my: 1 }} />

        {otherMicroAverages.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Other nutrients (period averages)
            </Typography>
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: 3 }}
              useFlexGap
            >
              {otherMicroAverages.map(({ key, avg }) => (
                <Chip
                  key={key}
                  label={`${labelForMicroKey(key)}: ${avg} g`}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Stack>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Daily Nutrition Data Table */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Daily progress
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Water</TableCell>
                <TableCell>Fiber</TableCell>
                <TableCell>Calories</TableCell>
                <TableCell>Protein</TableCell>
                <TableCell align="center">% of Goal</TableCell>
                <TableCell>Carbs</TableCell>
                <TableCell align="center">% of Goal</TableCell>
                <TableCell>Fats</TableCell>
                <TableCell align="center">% of Goal</TableCell>
                <TableCell>Weight</TableCell>
                <TableCell>Sleep</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {healthData.map((day, index) => {
                const reportData = day.reportData || {};
                const micro = reportData.micronutrients || {};
                const fiberDay =
                  typeof micro.fiber === "number" ? micro.fiber : 0;
                const calories =
                  (typeof reportData.kCal === "number" && reportData.kCal > 0
                    ? reportData.kCal
                    : null) ??
                  calculateCaloriesFromMacros(
                    reportData.proteins,
                    reportData.carbs,
                    reportData.fats,
                  );

                const proteinPercentage = Math.round(
                  calculatePercentage(
                    reportData.proteins,
                    nutritionGoal?.proteins,
                  ),
                );

                const carbsPercentage = Math.round(
                  calculatePercentage(reportData.carbs, nutritionGoal?.carbs),
                );

                const fatsPercentage = Math.round(
                  calculatePercentage(reportData.fats, nutritionGoal?.fats),
                );

                return (
                  <TableRow key={index}>
                    <TableCell>{formatDate(day.createdOn)}</TableCell>
                    <TableCell>{waterLabel(reportData.water || 0)}</TableCell>
                    <TableCell>
                      {fiberDay > 0 ? `${Math.round(fiberDay)}g` : "—"}
                    </TableCell>
                    <TableCell>{Math.round(calories)}</TableCell>
                    <TableCell>
                      {Math.round(reportData.proteins || 0)}g
                    </TableCell>
                    <TableCell align="center">
                      <LinearProgress
                        variant="determinate"
                        value={proteinPercentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: "grey.200",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: NewColors.nutrition.proteins,
                          },
                        }}
                      />
                      {proteinPercentage}%
                    </TableCell>
                    <TableCell>{Math.round(reportData.carbs || 0)}g</TableCell>
                    <TableCell align="center">
                      <LinearProgress
                        variant="determinate"
                        value={carbsPercentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: "grey.200",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: NewColors.nutrition.carbs,
                          },
                        }}
                      />
                      {carbsPercentage}%
                    </TableCell>
                    <TableCell>{Math.round(reportData.fats || 0)}g</TableCell>
                    <TableCell align="center">
                      <LinearProgress
                        variant="determinate"
                        value={fatsPercentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: "grey.200",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: NewColors.nutrition.fats,
                          },
                        }}
                      />
                      {fatsPercentage}%
                    </TableCell>
                    <TableCell>
                      {typeof reportData.weight === "number" &&
                      reportData.weight > 0
                        ? `${Math.round(reportData.weight)} lbs`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {typeof reportData.sleepHours === "number" &&
                      reportData.sleepHours > 0
                        ? `${reportData.sleepHours}h`
                        : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {daysWithCheckInQuestions.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Daily check-in questions
            </Typography>
            <Stack spacing={2.5}>
              {daysWithCheckInQuestions.map((day, index) => (
                <Box key={day.id || index}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    {formatDate(day.createdOn)}
                  </Typography>
                  <DailyOptionalQuestionsSection
                    optionalResponses={day.reportData?.optional}
                    organizationOptionals={organizationData?.optional}
                    loading={orgLoading}
                    showHeader={false}
                    compact
                  />
                </Box>
              ))}
            </Stack>
          </>
        )}
      </CardContentWrapper>
    </StyledCard>
  );
};

export default NutritionCard;
